import { Box, LoadingOverlay, Modal } from '@mantine/core'
import { FormattedMessage, useIntl } from 'react-intl'
import { getApiError } from '@/api/helpers/apiError'
import {
  useGetDatasetDetails,
  useInvalidateDatasetQueries,
  useUpdateDataset
} from '@/queries/datasetQueries'
import { showToast } from '@/theme/notifications'
import { DatasetForm, FormValues } from '../DatasetForm/DatasetForm'

type UpdateDatasetModalProps = {
  datasetId?: string
  opened: boolean
  onClose: () => void
}

export const UpdateDatasetModal = ({
  datasetId,
  opened,
  onClose
}: UpdateDatasetModalProps) => {
  const intl = useIntl()

  const { data: dataset, isLoading } = useGetDatasetDetails(
    datasetId || '',
    opened && !!datasetId
  )

  const hasFiles = (dataset?.media_files_count ?? 0) > 0

  const { mutateAsync: updateDataset, isPending } = useUpdateDataset()

  const { invalidateDatasetList, invalidateDatasetDetails } =
    useInvalidateDatasetQueries()

  const handleDatasetUpdate = async (values: FormValues) => {
    if (!datasetId) {
      return
    }

    const { name, description, datasetType, labelingType, labels } = values

    try {
      await updateDataset({
        datasetId,
        data: {
          name,
          description,
          dataset_type: datasetType,
          ...(hasFiles
            ? {}
            : {
                labeling_type: labelingType,
                labels: labels.map((label) => ({
                  name: label.name,
                  color: label.color
                }))
              })
        }
      })

      showToast(
        intl.formatMessage({ id: 'datasets.update.success' }),
        'success'
      )

      void invalidateDatasetList()
      void invalidateDatasetDetails(datasetId)

      onClose()
    } catch (err) {
      const { errorMessage } = getApiError(err)

      const message =
        errorMessage || intl.formatMessage({ id: 'datasets.update.error' })

      showToast(message, 'error')
    }
  }

  return (
    <Modal
      title={<FormattedMessage id="datasets.update" />}
      size="lg"
      opened={opened}
      onClose={onClose}
    >
      <Box pos="relative" mih={300}>
        <LoadingOverlay visible={isPending || isLoading} />

        {dataset && (
          <DatasetForm
            initialValues={{
              name: dataset.name,
              description: dataset.description,
              datasetType: dataset.dataset_type,
              labels: dataset.labels,
              labelingType: dataset.labeling_type
            }}
            submitLabel={<FormattedMessage id="saveChanges" />}
            isAnnotated={hasFiles}
            onCancel={onClose}
            onSubmit={(values) => void handleDatasetUpdate(values)}
          />
        )}
      </Box>
    </Modal>
  )
}
